import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import './CheckoutForm.css';

function CheckoutForm({ cart = [] }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentRequest, setPaymentRequest] = useState(null);

  const [orderNumber] = useState(() =>
    Math.floor(10000 + Math.random() * 90000).toString()
  );

  useEffect(() => {
    const cartTotal = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    setTotalAmount(cartTotal * 100); // Convert total to cents
  }, [cart]);

  useEffect(() => {
    if (!stripe || totalAmount === 0) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Black Canyon Tickets',
        amount: totalAmount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (e) => {
      try {
        const response = await fetch(
          'https://us-central1-black-canyon-tickets-bct.cloudfunctions.net/createPaymentIntent',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              currency: 'usd',
              customerEmail: e.payerEmail,
              customerName: e.payerName,
              customerPhone: e.payerPhone,
              customerAddress: {
                line1: e.paymentMethod.billing_details.address?.line1 || '',
                city: e.paymentMethod.billing_details.address?.city || '',
                state: e.paymentMethod.billing_details.address?.state || '',
                postal_code: e.paymentMethod.billing_details.address?.postal_code || '',
                country: 'US',
              },
              description: `BCT Order #${orderNumber}`,
            }),
          }
        );

        const { clientSecret } = await response.json();

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: e.paymentMethod.id },
          { handleActions: false }
        );

        if (error) {
          e.complete('fail');
          setMessage(error.message);
          return;
        }

        e.complete('success');

        if (paymentIntent.status === 'succeeded') {
          navigate('/order-confirmation', {
            state: {
              fromCheckout: true,
              name: e.payerName,
              email: e.payerEmail,
              phone: e.payerPhone || '',
              address: {
                line1: e.paymentMethod.billing_details.address?.line1 || '',
                city: e.paymentMethod.billing_details.address?.city || '',
                state: e.paymentMethod.billing_details.address?.state || '',
                postal_code: e.paymentMethod.billing_details.address?.postal_code || '',
                country: 'US',
              },
              orderNumber,
              total: totalAmount / 100,
              tickets: cart.map((item) => ({
                eventName: item.event,
                ticketType: item.ticketType,
                price: item.price,
                quantity: item.quantity,
              })),
              paymentIntentId: paymentIntent.id,
            },
          });
        }
      } catch (err) {
        e.complete('fail');
        setMessage('An error occurred. Please try again.');
      }
    });
  }, [stripe, totalAmount]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    if (email !== confirmEmail) {
  setMessage('Email addresses do not match. Please check and try again.');
  return;
}
if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
  setMessage('Please enter a valid email address.');
  return;
}
    setLoading(true);

    const cardElement = elements.getElement(CardElement);

    try {
      const response = await fetch(
        'https://us-central1-black-canyon-tickets-bct.cloudfunctions.net/createPaymentIntent',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: totalAmount,
            currency: 'usd',
            customerEmail: email,
            customerName: name,
            customerPhone: phone,
            customerAddress: address,
            description: `BCT Order #${orderNumber}`,
          }),
        }
      );

      const { clientSecret } = await response.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name,
            email,
            phone,
            address,
          },
        },
      });

      if (result.error) {
        setMessage(result.error.message);
      } else if (
        result.paymentIntent &&
        result.paymentIntent.status === 'succeeded'
      ) {
        const paymentIntentId = result.paymentIntent.id;

        navigate('/order-confirmation', {
          state: {
            fromCheckout: true,
            name,
            email,
            phone,
            address,
            orderNumber,
            total: totalAmount / 100,
            tickets: cart.map((item) => ({
              eventName: item.event,
              ticketType: item.ticketType,
              price: item.price, // Include the price
              quantity: item.quantity,
            })),
            paymentIntentId,
          },
        });
      }
    } catch (error) {
      setMessage('An error occurred. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-layout">
      <form onSubmit={handleSubmit} className="checkout-form">
        <h3>Payment Details</h3>
        {paymentRequest && (
          <div style={{ marginBottom: '20px' }}>
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              style={{ paymentRequestButton: { height: '48px' } }}
            />
            <div style={{ textAlign: 'center', margin: '16px 0', color: '#777', fontSize: '13px' }}>
              — or pay with card below —
            </div>
          </div>
        )}
        <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input type="email" placeholder="Confirm Email Address" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} required />
        <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        <input type="text" placeholder="Address Line 1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} required />
        <div className="checkout-row">
          <input type="text" placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} required />
          <input type="text" placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} required />
        </div>
        <div className="checkout-row">
          <input type="text" placeholder="Postal Code" value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} />
          <input type="text" placeholder="Country" value={address.country} disabled />
        </div>
        <div className="card-element-container">
          <CardElement options={{ style: { base: { color: '#fff', fontSize: '16px', '::placeholder': { color: '#777' } } } }} />
        </div>
        <button type="submit" className="pay-now-button" disabled={!stripe || loading}>
          {loading ? 'Processing...' : `Pay Now ($${(totalAmount / 100).toFixed(2)})`}
        </button>
        {message && <div className="payment-message">{message}</div>}
      </form>

      <div className="checkout-cart-summary">
        <h3>Order Summary</h3>
        {cart.length === 0 ? (
          <p style={{ color: '#777' }}>No items in cart.</p>
        ) : (
          <>
            <div className="checkout-cart-items">
              {cart.map((item, index) => (
                <div key={index} className="checkout-cart-item">
                  <div className="checkout-cart-item-info">
                    <span className="checkout-cart-event">{item.event}</span>
                    <span className="checkout-cart-type">{item.ticketType}</span>
                  </div>
                  <div className="checkout-cart-item-right">
                    <span className="checkout-cart-qty">x{item.quantity}</span>
                    <span className="checkout-cart-price">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="checkout-cart-divider" />
            <div className="checkout-cart-total">
              <span>Total</span>
              <span>${(totalAmount / 100).toFixed(2)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CheckoutForm;
